import React, { useState, useRef, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ShieldCheck, Globe, Settings, Cpu, ChevronRight } from "lucide-react";
import Footer from "../../components/Footer";

// CSS to hide the scrollbar (the "line") globally for the nav component
const hideScrollbarStyle = {
  msOverflowStyle: "none",
  scrollbarWidth: "none",
  WebkitScrollbar: { display: "none" },
};

// --- Hero Pillar Cards Data ---
const heroCards = [
  {
    title: "Zero Trust Architecture",
    lines: [
      "No user, device, or app is trusted by default",
      "Continuous identity verification",
      "Least-privilege access everywhere",
    ],
  },
  {
    title: "Operationalized Security Stack",
    lines: [
      "Unified SASE, Identity, Email & Data security",
      "Continuous tool optimization",
      "One high-performance security platform",
    ],
  },
  {
    title: "End-to-End Detection & Response",
    lines: [
      "Real-time threat monitoring",
      "Proactive threat hunting",
      "Automated incident response",
    ],
  },
  {
    title: "Continuous Compliance",
    lines: [
      "Always audit-ready",
      "Policy & framework alignment",
      "Ongoing compliance enforcement",
    ],
  },
];

// --- Service Data ---
const managedServices = [
  {
    id: "Managed SASE|SSE",
    title: "Managed SASE|SSE",
    path: "/services/managed-services/sase",
    icon: <ShieldCheck size={20} />,
    heading: "Managed SASE|SSE",
    description: [
      "Network and security are no longer separate disciplines yet most organizations still operate them as disconnected silos.Users are everywhere. Applications live in SaaS and cloud. Data flows outside the perimeter. But security controls remain fragmented across VPNs, firewalls, SWGs, CASBs, and endpoint agents that were never designed to work as one.",
      "The result: inconsistent policy enforcement, poor user experience, blind spots in traffic visibility, and security teams constantly firefighting access issues instead of reducing risk.",
      "Synthera delivers Managed SASE as an always-on, outcome-focused service not just a platform deployment.We design, operate, optimize, and continuously govern your SASE environment so access is secure, fast, and enforceable everywhere without operational chaos.",
    ],
    imageUrl:
      "https://res.cloudinary.com/dpn3bkjba/image/upload/v1769870172/Managed_SASE_u42dcm.png",
  },
  {
    id: "Managed Data Security",
    title: "Managed Data Security",
    path: "/services/managed-services/data-security",
    icon: <Globe size={20} />,
    heading: "Managed Data Security",
    description: [
      "Data is the asset attackers want, but in most organizations, data security is fragmented, reactive, and poorly owned.",
      "Sensitive data spreads across files, SaaS applications, collaboration tools, and business systems, while visibility and accountability lag behind.",
      "Synthera operates Data Security as part of unified Managed Security Services, ensuring sensitive data is continuously discovered, monitored, protected, and governed, not classified once and forgotten.",
    ],
    imageUrl:
      "https://res.cloudinary.com/dpn3bkjba/image/upload/v1769870172/Managed_Data_Security_e8k1bj.png",
  },
  {
    id: "Managed Identity Security",
    title: "Managed Identity Security",
    path: "/services/managed-services/identity-security",
    icon: <Settings size={20} />,
    heading: "Managed Identity Security",
    description: [
      "Identity is the control plane of modern security and it is also the most misconfigured, over-trusted, and under-operated layer in most organizations.",
      "Users, service accounts, APIs, workloads, and machines all authenticate somewhere. Yet ownership of identity security is fragmented across IAM teams, IT operations, security, and vendors. The result is excessive access, silent privilege creep, weak enforcement, and delayed response when identities are abused.",
      "Synthera operates Identity Security as a continuously managed discipline, not a one-time IAM deployment. We ensure identities are continuously governed, monitored, and risk-reduced — across workforce, privileged, cloud, SaaS, and machine identities.",
    ],
    imageUrl:
      "https://res.cloudinary.com/dpn3bkjba/image/upload/v1771579557/managed_identity_security_weobs4.svg",
  },
  {
    id: "Managed Email Security",
    title: "Managed Email Security",
    path: "/services/managed-services/email-security",
    icon: <Cpu size={20} />,
    heading: "Managed Email Security",
    description: [
      "Email remains the primary entry point for breaches, fraud, and data loss yet in most organizations, email security is treated as a static gateway problem rather than a continuously operated risk surface.",
      "Modern email threats do not rely on obvious malware. They exploit human trust, identity weaknesses, legitimate SaaS links, AI-generated lures, and slow, low-signal campaigns that bypass traditional filters. Meanwhile, ownership of email security is fragmented between IT, security, and users leaving gaps attackers thrive in.",
      "Synthera operates Email Security as a continuously managed service, ensuring threats are detected, investigated, and contained across inbound, outbound, and internal email — not just filtered at the perimeter.",
    ],
    imageUrl:
      "https://res.cloudinary.com/dpn3bkjba/image/upload/v1769870172/Managed_Email_Security_avnkdx.png",
  },
  // {
  //   id: "Managed Detection And Response",
  //   title: "Managed Detection And Response",
  //   path: "/services/managed-services/detection-and-response",
  //   icon: <Cpu size={20} />,
  //   heading: "Managed Detection and Response",
  //   description: [
  //     "Security Operations is the central nervous system of defense — yet for most organizations, it is overwhelmed by noise, blind spots, and burnout.",
  //     "Endpoints, networks, clouds, and SaaS applications generate endless telemetry. Yet ownership of detection is fragmented across siloed tools, understaffed SOCs, and rigid compliance mandates. The result is alert fatigue, missed signals, slow reaction times, and attackers who dwell in environments for weeks before being noticed.",
  //     "Synthera operates Detection and Response as a proactive, outcome-driven discipline, not a passive monitoring service. We ensure threats are continuously hunted, validated, contained, and remediated — across your entire digital estate.",
  //   ],
  //   imageUrl:
  //     "https://res.cloudinary.com/dpn3bkjba/image/upload/v1769870173/Managed_detection_and_Response_ljhih0.png",
  // },
];

// --- StickyTabNav Component ---
const StickyTabNav = ({ activeTab, onTabClick, mainNavHeight }) => {
  const containerRef = useRef(null);
  const scrollRef = useRef(null);
  const activeBtnRef = useRef(null);
  const [isSticky, setIsSticky] = useState(false);
  const [originalTop, setOriginalTop] = useState(0);
  const [navHeight, setNavHeight] = useState(0);

  useEffect(() => {
    if (activeBtnRef.current && scrollRef.current) {
      activeBtnRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, [activeTab]);

  useEffect(() => {
    const updatePosition = () => {
      if (containerRef.current) {
        setOriginalTop(containerRef.current.offsetTop);
        setNavHeight(containerRef.current.offsetHeight);
      }
    };
    updatePosition();
    window.addEventListener("resize", updatePosition);
    return () => window.removeEventListener("resize", updatePosition);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const safeNavHeight = mainNavHeight || 0;
      if (window.scrollY < 10) {
        setIsSticky(false);
        return;
      }
      const threshold = originalTop - safeNavHeight;
      setIsSticky(window.scrollY >= threshold);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [originalTop, mainNavHeight]);

  return (
    <div ref={containerRef} className="relative w-full z-40 px-4 md:px-0">
      <nav
        className={`transition-all duration-300 mx-auto max-w-6xl ${
          isSticky ? "fixed left-0 right-0 shadow-2xl" : "relative -mt-10"
        }`}
        style={
          isSticky
            ? { top: `${mainNavHeight}px`, transform: "translateZ(0)" }
            : {}
        }
      >
        <div className="bg-[#0e2a25] rounded-full px-2 md:px-4 py-1.5 md:py-2 flex items-center">
          <div
            ref={scrollRef}
            // FIXED: Applied 'hideScrollbarStyle' to resolve ESLint warning
            className="flex items-center overflow-x-auto no-scrollbar gap-5 w-full"
            style={hideScrollbarStyle}
          >
            <style>
              {`
                .no-scrollbar::-webkit-scrollbar {
                  display: none;
                }
              `}
            </style>
            {managedServices.map((service) => (
              <button
                key={service.id}
                ref={activeTab === service.id ? activeBtnRef : null}
                onClick={() => onTabClick(service.id)}
                className={`flex items-center gap-3 px-6 py-2.5 rounded-full transition-all whitespace-nowrap text-xs md:text-sm font-medium
                  ${
                    activeTab === service.id
                      ? "bg-[#112d28] text-[#24ff81]"
                      : "text-gray-400 hover:text-white"
                  }`}
              >
                <span
                  className={
                    activeTab === service.id
                      ? "text-[#24ff81]"
                      : "text-gray-500"
                  }
                >
                  {service.icon}
                </span>
                {service.title}
              </button>
            ))}
          </div>
        </div>
      </nav>
      {isSticky && <div style={{ height: `${navHeight}px` }} />}
    </div>
  );
};

// --- ServiceSection Component ---
const ServiceSection = React.forwardRef(({ service, index }, ref) => (
  <section
    ref={ref}
    id={service.id}
    className="py-24 border-b border-gray-100 last:border-0"
  >
    <div className="container mx-auto max-w-6xl px-6">
      <div
        className={`flex flex-col md:flex-row items-center gap-16 ${
          index % 2 !== 0 ? "md:flex-row-reverse" : ""
        }`}
      >
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="w-full md:w-1/2"
        >
          <h2 className="text-4xl font-bold text-[#0c3f2f] mb-6">
            {service.heading}
          </h2>
          {Array.isArray(service.description) ? (
            <div className="space-y-4 mb-8">
              {service.description.map((para, i) => (
                <p key={i} className="text-gray-600 text-lg leading-relaxed">
                  {para}
                </p>
              ))}
            </div>
          ) : (
            <p className="text-gray-600 text-lg leading-relaxed mb-8">
              {service.description}
            </p>
          )}

          <ul className="space-y-4 mb-8">
            {service.points?.map((pt, i) => (
              <li
                key={i}
                className="flex items-center gap-3 text-gray-700 font-medium"
              >
                <ChevronRight className="text-[#24ff81]" size={18} /> {pt}
              </li>
            ))}
          </ul>
          <Link
            to={service.path}
            className="inline-block bg-[#013624] text-white px-8 py-2 rounded-md font-bold hover:bg-green-800 transition-all no-underline"
          >
            More Information &rarr;
          </Link>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="w-full md:w-1/2 flex justify-center"
        >
          <div className="relative">
            <img
              src={service.imageUrl}
              alt={service.title}
              className="w-full max-w-md h-auto object-contain z-10 relative"
            />
            <div className="absolute inset-0 bg-green-50 rounded-full blur-3xl scale-125 -z-0 opacity-50" />
          </div>
        </motion.div>
      </div>
    </div>
  </section>
));

export default function ManagedSecurityServices() {
  const [activeTab, setActiveTab] = useState(managedServices[0].id);
  const [mainNavHeight, setMainNavHeight] = useState(0);

  const sectionRefs = useRef(
    managedServices.reduce(
      (acc, s) => ({ ...acc, [s.id]: React.createRef() }),
      {},
    ),
  );

  useEffect(() => {
    const nav = document.getElementById("main-navbar");
    if (nav) setMainNavHeight(nav.offsetHeight);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const offset = mainNavHeight + 100;
      for (const s of managedServices) {
        const ref = sectionRefs.current[s.id];
        if (ref.current) {
          const { top, bottom } = ref.current.getBoundingClientRect();
          if (top <= offset && bottom >= offset) {
            setActiveTab(s.id);
            break;
          }
        }
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [mainNavHeight]);

  const handleTabClick = (id) => {
    const ref = sectionRefs.current[id];
    if (ref.current) {
      const y =
        ref.current.getBoundingClientRect().top +
        window.scrollY -
        mainNavHeight -
        80;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  return (
    <>
      <Helmet>
        <title>Managed Security Services | Synthera Security</title>

        <meta
          name="description"
          content="Managed Security Services from Synthera Security including Managed SASE, Managed Data Security, Managed Identity Security, and Managed Email Security for modern enterprises."
        />

        <meta
          name="keywords"
          content="Managed Security Services, Managed SASE, Managed Data Security, Managed Identity Security, Managed Email Security, Cybersecurity Services, Zero Trust Security, Synthera Security"
        />

        <meta name="robots" content="index, follow" />

        <meta
          property="og:title"
          content="Managed Security Services | Synthera Security"
        />

        <meta
          property="og:description"
          content="Comprehensive managed cybersecurity services covering SASE, identity security, data protection, email security, and Zero Trust operations."
        />

        <meta property="og:type" content="website" />

        <meta
          property="og:url"
          content="https://www.syntherasecurity.com/services/managed-security-services"
        />

        <link
          rel="canonical"
          href="https://www.syntherasecurity.com/services/managed-security-services"
        />

        <meta
          property="og:image"
          content="https://res.cloudinary.com/dpn3bkjba/image/upload/v1769870172/Managed_SASE_u42dcm.png"
        />
      </Helmet>

      <div className="bg-white min-h-screen font-sans">
        {/* --- HERO SECTION --- */}
        <div className="bg-[#032d28] pt-32 pb-40">
          <div className="container mx-auto px-6">
            <div className="text-center mb-20">
              <h1 className="text-white text-5xl md:text-7xl font-bold mb-6 tracking-tight">
                Synthera Managed Services
              </h1>
              <p className="text-green-100/40 text-xs tracking-[0.2em] uppercase font-bold mb-4">
                Building Trust on a Foundation of Zero Default
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {heroCards.map((card, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -8 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{
                    delay: index * 0.05,
                    duration: 0.12,
                    ease: "easeOut",
                    type: "tween",
                  }}
                  className="group bg-white p-8 rounded-3xl shadow-lg flex flex-col min-h-[300px] cursor-pointer transition-all hover:shadow-2xl"
                >
                  <h3 className="text-[#34d399] text-lg font-bold uppercase tracking-wide mb-6 group-hover:text-[#0c3f38] transition">
                    {card.title}
                  </h3>
                  <div className="flex flex-col w-full">
                    {card.lines.map((line, i) => (
                      <div key={i} className="w-full">
                        <p className="text-[#0c3f38]/70 text-sm font-medium py-2 group-hover:text-[#0c3f38] transition">
                          {line}
                        </p>
                        {i !== card.lines.length - 1 && (
                          <div className="h-px w-full bg-[#34d399]/40 my-1" />
                        )}
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        <StickyTabNav
          activeTab={activeTab}
          onTabClick={handleTabClick}
          mainNavHeight={mainNavHeight}
        />

        <main>
          {managedServices.map((service, index) => (
            <ServiceSection
              key={service.id}
              service={service}
              index={index}
              ref={sectionRefs.current[service.id]}
            />
          ))}

          {/* --- ADDED: CERTIFICATION SECTION --- */}
          <section className="py-24 bg-slate-50 border-t border-gray-100">
            <div className="container mx-auto px-6 max-w-6xl text-center">
              <h4 className="text-xl md:text-2xl font-bold text-[#0c3f2f] mb-12 max-w-4xl mx-auto leading-relaxed">
                Our capabilities are backed by more than 500 technical
                certifications and credentials across enterprise cybersecurity,
                AI, cloud, and DevOps:
              </h4>
              <div className="flex justify-center items-center">
                <img
                  src="https://res.cloudinary.com/dpn3bkjba/image/upload/v1769604865/managed_last_logo_humxgz.png"
                  alt="Synthera technical certifications and partner logos"
                  className="max-w-full h-auto object-contain drop-shadow-sm"
                />
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}
